import { ReactNode, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { extractBackendErrorMessage } from '@/services/hsm-backend';

export interface CancelWithConfirmProps {
  /** Label of the trigger button. Defaults to "Cancelar". */
  buttonLabel?: string;
  /** Confirmation dialog title. */
  title: string;
  /** Confirmation dialog body. */
  description: ReactNode;
  /** Label of the confirm button. Defaults to "Confirmar cancelación". */
  confirmLabel?: string;
  /** Snackbar text on success. */
  successMessage: string;
  /** Prefix for the error snackbar, e.g. "No se pudo cancelar la suscripción". */
  errorMessagePrefix: string;
  /** Performs the cancellation (the service call). */
  onConfirm: () => Promise<unknown>;
  /** Called after a successful cancellation (typically the shell's refetch). */
  onConfirmed?: () => Promise<void> | void;
}

/**
 * Small "Cancelar" button that opens a confirmation dialog and runs a
 * cancellation. Shared by the billing surfaces (subscription / plan / credit
 * line / package). Handles the submitting state, the success/error snackbars
 * and the post-success refresh, so each call site only wires the copy, the
 * service call and the refetch.
 */
export const CancelWithConfirm = ({
  buttonLabel = 'Cancelar',
  title,
  description,
  confirmLabel = 'Confirmar cancelación',
  successMessage,
  errorMessagePrefix,
  onConfirm,
  onConfirmed,
}: CancelWithConfirmProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } catch (error) {
      enqueueSnackbar(
        `${errorMessagePrefix}: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
      setSubmitting(false);
      return;
    }

    // Cancellation succeeded: notify and close BEFORE the refetch. The refetch
    // typically removes the resource's "cancelable" state and unmounts this
    // component, so any state update must happen first. A failed refetch is
    // NOT a cancellation failure (the shell surfaces its own refresh warning),
    // so it must not trigger the error snackbar above.
    enqueueSnackbar(successMessage, { variant: 'success' });
    setSubmitting(false);
    setOpen(false);

    try {
      await onConfirmed?.();
    } catch {
      // Swallow: the cancellation already succeeded.
    }
  };

  return (
    <>
      <Button
        size="small"
        color="error"
        variant="text"
        onClick={() => setOpen(true)}
      >
        {buttonLabel}
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!submitting) {
            setOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            Volver
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
